import {
  type AccessCodeEntry,
  type AccessCodesView,
  GIVEN_ACCESS_KINDS,
  type GivenAccessKind,
} from '@gardener/protocol';
import { createResource, createSignal, For, Match, onMount, Show, Switch } from 'solid-js';
import { call } from './api';
import { route } from './nav';
import {
  ago,
  ask,
  Copy,
  EmptyState,
  Icon,
  IconButton,
  LoadingLine,
  optimistic,
  SETTINGS_INPUT,
  Segmented,
  SettingsCard,
  SettingsItem,
  toast,
  Well,
  when,
} from './ui';

/** What each kind is for (ADR-0135): its /i/ link brings new accounts, which keep it. */
const KINDS: Record<GivenAccessKind, string> = {
  creator: 'A streamer’s own: its link fills in their name on the welcome.',
  playtest: 'The playtesters’ shared one.',
  channel: 'A post’s /i/ link: the label (where it was posted) never reaches a browser.',
};
/** A kind's badge tone: creators in their check's purple, playtests blue, channels neutral. */
const TONE: Record<GivenAccessKind, string> = {
  creator: '[--badge-tone:var(--color-violet-400)]',
  playtest: '[--badge-tone:var(--color-sky-400)]',
  channel: '',
};
const name = (k: GivenAccessKind) => k[0]?.toUpperCase() + k.slice(1);
const KIND_OPTIONS = GIVEN_ACCESS_KINDS.map((k) => [k, name(k)] as const);
const link = (c: AccessCodeEntry) => `gaws.io/i/${c.key.toLowerCase()}`;

/** A settings item's label and its control side by side (logdash's `ProjectInfoCard` rows). */
const FIELD = 'flex min-w-0 items-center gap-3';
/** The label's column. */
const LABEL = 'text-fg-muted w-16 shrink-0';
/** A field's input: logdash's `Input` (size sm) as its settings cards draw it. */
const INPUT = `-my-1.5 w-full max-w-64 ${SETTINGS_INPUT}`;
/** The codes' columns (code, for, kind, link, made, delete); a narrow page drops the link's text and when it was made. */
const GRID =
  'grid grid-cols-[auto_minmax(0,1fr)_auto_auto_auto] gap-x-4 @2xl:grid-cols-[auto_minmax(0,1fr)_auto_auto_auto_auto]';
/** A row on those columns: the header (logdash's breakdown columns, `CARD_COLUMNS`) or a code. */
const ROW = 'col-span-full grid grid-cols-subgrid items-center px-3';

/**
 * Access codes (ADR-0135, kept in the database since ADR-0150): every one, a new one, one deleted. A code works (or
 * stops) the moment it is saved; accounts it brought keep it. Added and deleted at once on the page, taken back if the
 * server refuses (ADR-0154).
 */
export function Codes() {
  const [list, { mutate }] = createResource(() => call<AccessCodesView>('GET', '/api/admin/access-codes'));
  /** The list, or nothing while it loads or after it failed (reading a failed resource throws). */
  const codes = () => (list.error ? undefined : list());
  const [code, setCode] = createSignal('');
  const [label, setLabel] = createSignal('');
  const [kind, setKind] = createSignal<GivenAccessKind>('creator');
  /** Codes added and not answered yet (shown faded). */
  const [pending, setPending] = createSignal<string[]>([]);
  let first!: HTMLInputElement;
  onMount(() => {
    if (route().arg === 'new') first.focus();
  });

  async function add(e: SubmitEvent) {
    e.preventDefault();
    const typed = { code: code().trim(), label: label().trim(), kind: kind() };
    if (!typed.code || !typed.label) return toast('A code and who it is for', 'error');
    const key = typed.code.toUpperCase();
    const draft: AccessCodeEntry = { ...typed, key, at: new Date().toISOString() };
    await optimistic(
      () => {
        const before = codes();
        mutate({ codes: [draft, ...(before?.codes ?? [])] });
        setPending((p) => [...p, key]);
        setCode('');
        setLabel('');
        return () => {
          mutate(before);
          setCode(typed.code);
          setLabel(typed.label);
        };
      },
      () => call<AccessCodeEntry>('POST', '/api/admin/access-codes', typed),
      (made) => {
        mutate((l) => ({ codes: (l?.codes ?? []).map((c) => (c === draft ? made : c)) }));
        toast(`${made.code} works now`);
      },
    );
    setPending((p) => p.filter((k) => k !== key));
  }
  async function remove(c: AccessCodeEntry) {
    const sure = await ask({
      title: `Delete ${c.code}?`,
      body: `${c.label}'s code stops working at once. Accounts it brought keep it.`,
      confirm: 'Delete',
      danger: true,
    });
    if (sure === null) return;
    await optimistic(
      () => {
        const before = codes();
        mutate({ codes: (before?.codes ?? []).filter((x) => x.key !== c.key) });
        return () => mutate(before);
      },
      () => call('DELETE', `/api/admin/access-codes/${encodeURIComponent(c.key)}`),
      () => toast(`${c.code} is deleted`),
    );
  }

  return (
    <div class="@container flex w-full flex-col gap-2 p-2">
      <form aria-label="New code" onSubmit={add}>
        <SettingsCard title="New code" description={KINDS[kind()]}>
          <SettingsItem>
            <div class={FIELD}>
              <span class={LABEL}>Kind</span>
              <div class="-my-1.5">
                <Segmented value={kind()} options={KIND_OPTIONS} onChange={setKind} />
              </div>
            </div>
          </SettingsItem>
          <SettingsItem>
            <label class={FIELD}>
              <span class={LABEL}>Code</span>
              <input
                ref={first}
                class={INPUT}
                data-size="sm"
                value={code()}
                onInput={(e) => setCode(e.currentTarget.value)}
                placeholder="SPROUT7Q"
                autocomplete="off"
                spellcheck={false}
              />
            </label>
          </SettingsItem>
          <SettingsItem
            action={
              <button type="submit" class="ld-button" data-variant="primary" data-size="sm">
                <Icon name="plus" class="size-4" />
                Add
              </button>
            }
          >
            <label class={FIELD}>
              <span class={LABEL}>For</span>
              <input
                class={INPUT}
                data-size="sm"
                value={label()}
                onInput={(e) => setLabel(e.currentTarget.value)}
                placeholder="Sprouty"
                autocomplete="off"
              />
            </label>
          </SettingsItem>
        </SettingsCard>
      </form>

      {/* logdash's API keys Well, its rows on one grid so the columns line up as a table's. */}
      <Well
        title="Access codes"
        actions={
          <Show when={codes()}>
            {(l) => (
              <span class="text-fg-muted px-3 text-xs tabular-nums">
                {l().codes.length} {l().codes.length === 1 ? 'code' : 'codes'}
              </span>
            )}
          </Show>
        }
      >
        <Switch>
          <Match when={list.error}>
            <div class="text-fg-muted flex items-center gap-2 px-3 py-2.5 text-[13px]">
              <Icon name="danger" class="size-4 shrink-0" />
              {(list.error as Error).message}
            </div>
          </Match>
          <Match when={!codes()}>
            <div class="px-3 py-2.5">
              <LoadingLine label="Loading codes" />
            </div>
          </Match>
          <Match when={!codes()?.codes.length}>
            <EmptyState
              centered
              title="No codes yet"
              description="Add one above: its gaws.io/i/ link brings new accounts, and they keep the code."
            />
          </Match>
          <Match when={codes()?.codes.length}>
            <div class={GRID}>
              <div class={`${ROW} text-fg-muted h-6 text-xs`}>
                <span>Code</span>
                <span>For</span>
                <span>Kind</span>
                <span>Link</span>
                <span class="hidden @2xl:block">Made</span>
                <span />
              </div>
              <ul class="edge-between contents">
                <For each={codes()?.codes}>
                  {(c) => (
                    <li
                      class={`${ROW} min-h-12 py-2.5 text-[13px]`}
                      classList={{
                        'opacity-50': pending().includes(c.key),
                        'bg-surface-25-selected-bg rounded-lg': route().arg === c.key,
                      }}
                      aria-current={route().arg === c.key ? 'true' : undefined}
                    >
                      <span class="font-mono font-medium">{c.code}</span>
                      <span class="truncate">{c.label}</span>
                      <span class={`ld-badge ${TONE[c.kind]}`} data-size="sm">
                        {name(c.kind)}
                      </span>
                      <span class="flex min-w-0 items-center gap-1">
                        <span class="text-fg-tertiary hidden truncate @2xl:block">{link(c)}</span>
                        <Copy text={`https://${link(c)}`} label="Copy the link" well />
                      </span>
                      <span class="text-fg-muted hidden whitespace-nowrap @2xl:block" title={when(c.at)}>
                        {ago(c.at)}
                      </span>
                      <IconButton label={`Delete ${c.code}`} danger well class="-mr-1.5" onClick={() => void remove(c)}>
                        <Icon name="trash" class="size-4" />
                      </IconButton>
                    </li>
                  )}
                </For>
              </ul>
            </div>
          </Match>
        </Switch>
      </Well>
    </div>
  );
}
